
import React, { createContext, useContext, useState, useEffect, FC, ReactNode } from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getUserById } from '@/services/userService';
import { User } from '@/types';
import { syncUserPoints } from '@/services/storePointService';

// Define o perfil de usuário estendido, incluindo dados personalizados
interface ExtendedUser extends User {
  connectedAccountId?: string | null;
  points?: number;
  dbkey?: string | null;
  pointsLastSynced?: string | null;
  full_name?: string;
  linkStatus?: string | null;
  linkExpirationReason?: string | null;
}

interface DartsBeatAuthResult {
  success: boolean;
  error?: string;
  errorCode?: string;
  isAdmin: boolean;
  points?: number;
}

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: ExtendedUser | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  refreshUserProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ data: unknown; error: unknown; isAdmin: boolean }>;
  signInWithDartsBeat: (email: string, password: string) => Promise<DartsBeatAuthResult>;
  resetPassword: (email: string) => Promise<{ data?: unknown; error?: unknown }>;
  updateUserPassword: (password: string) => Promise<{ data?: unknown; error?: unknown }>;
}

// Create the context
const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  session: null,
  login: async () => ({ success: false }),
  logout: async () => {},
  loading: true,
  isAdmin: false,
  isLoading: true,
  refreshUserProfile: async () => {},
  signIn: async () => ({ data: null, error: null, isAdmin: false }),
  signInWithDartsBeat: async () => ({ success: false, isAdmin: false }),
  resetPassword: async () => ({}),
  updateUserPassword: async () => ({}),
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<ExtendedUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Função para buscar o perfil do usuário usando safe view
  const fetchUserProfile = async (userId: string) => {
    try {
      // First, get current user metadata to check admin role
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Error fetching auth user:', authError);
        setIsAdmin(false);
        return;
      }

      // Determine admin status from user metadata initially
      const isUserAdminFromAuth = authUser?.app_metadata?.role === 'admin' || false;

      // Then fetch profile data from database to check profiles.role as well
      const { data: profile, error: profileError } = await supabase
        .from('profile_safe')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Also check profiles.role, status, and username directly from profiles table
      const { data: profileInfo, error: profileInfoError } = await supabase
        .from('profiles')
        .select('role, status, username')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        // Don't fail completely if profile fetch fails, continue with admin check
      }

      // Determine final admin status from both sources
      const isUserAdminFromProfile = profileInfo?.role === 'admin' || false;
      const isUserAdmin = isUserAdminFromAuth || isUserAdminFromProfile;

      // Check if user account is inactive - if so, sign out immediately
      if (profileInfo?.status === 'inactive') {
        await supabase.auth.signOut();
        // Show user-friendly error message
        setTimeout(() => {
          // Using a toast or alert here would be better, but we don't have access to toast here
          alert('Conta suspensa. Entre em contacto com o suporte para reativação.');
        }, 100);
        throw new Error('Conta suspensa. Entre em contacto com o suporte para reativação.');
      }


      // Set admin status
      setIsAdmin(isUserAdmin);

      if (profile) {
        // Transformar os dados para o formato correto (sem dbkey para segurança)
        const extendedUser: ExtendedUser = {
          id: profile.id,
          name: profile.display_name || profile.email || '',
          full_name: profile.display_name || '',
          username: profileInfo?.username || profile.display_name || profile.email || '',
          email: profile.email || '',
          avatar: profile.avatar_url || '',
          role: isUserAdmin ? 'admin' : 'user',
          createdAt: profile.created_at || '',
          status: profileInfo?.status || 'active',
          connectedAccountId: profile.connected_account_id || null,
          points: profile.points || 0,
          dbkey: null, // Never expose dbkey to client
          pointsLastSynced: profile.points_last_synced || null,
          linkStatus: profile.link_status || null,
          linkExpirationReason: profile.link_expiration_reason || null,
        };

        setUserProfile(extendedUser);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setIsAdmin(false);
      setUserProfile(null);
    }
  };

  // Função para atualizar o perfil de usuário
  const refreshUserProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  useEffect(() => {
    // Configurar listener para alterações no estado de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user || null);

      if (session?.user) {
        // Buscar o perfil do usuário quando a sessão está disponível
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
        setIsAdmin(false);
      }
    });

    // Verificar inicialmente se há uma sessão
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user || null);

      if (session?.user) {
        fetchUserProfile(session.user.id);
      }

      setLoading(false);
      setIsInitialized(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-sync points balance on visibility change and periodic polling
  useEffect(() => {
    // Only sync if user is logged in AND has an active DartsBeat connection
    // Don't sync if connection is unlinked or expired
    if (!user || !userProfile?.connectedAccountId || userProfile?.linkStatus === 'unlinked' || userProfile?.linkStatus === 'expired') return;

    // Helper to sync with DartsBeat API and refresh local profile
    const syncWithDartsBeat = async () => {
      try {
        const result = await syncUserPoints(user.id); // Call DartsBeat API
        await refreshUserProfile(); // Refresh local data so link_status updates

        if (!result.success && result.linkExpired) {
          console.warn('DartsBeat connection marked as expired during auto-sync');
        }
      } catch (error) {
        console.error('Auto-sync failed:', error);
      }
    };

    // Sync when user returns to tab (switched from another tab/window)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('⚡ Tab became visible - syncing with DartsBeat...');
        syncWithDartsBeat();
      }
    };

    // Background polling every 2 minutes (120 seconds) while user is active
    const syncInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('⏰ Periodic sync - refreshing balance from DartsBeat...');
        syncWithDartsBeat();
      }
    }, 120000); // 120 seconds (2 minutes)

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(syncInterval);
    };
  }, [user, userProfile?.connectedAccountId]);

  // Login com email e senha
  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('Error logging in:', errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async () => {
    // User-initiated sign-out: if currently linked to DartsBeat, also
    // call the unlink action so DartsBeat-side state, the local profile,
    // and the audit trail all reflect the disconnect. The next login will
    // re-link automatically through dartsbeat-auth (auth_join row).
    //
    // Fail-open on unlink: a backend error does not block the sign-out.
    // The user asked to leave, and the next login reconciles state anyway.
    // The forced-suspension paths in this file call supabase.auth.signOut()
    // directly and bypass this helper, so they are unaffected.
    if (userProfile?.connectedAccountId && userProfile?.linkStatus !== 'unlinked') {
      try {
        const { error } = await supabase.functions.invoke('dartsbeat-api', {
          body: { action: 'unlink' }
        });
        if (error) console.error('Unlink during logout failed:', error);
      } catch (e) {
        console.error('Unlink during logout threw:', e);
      }
    }
    await supabase.auth.signOut();
  };

  // Implementation for signIn (used by LoginForm)
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { data: null, error, isAdmin: false };
      }

      if (!data.user) {
        return { data: null, error: new Error('Authentication failed'), isAdmin: false };
      }

      // Auth-side admin signal (some admins are flagged via app_metadata).
      const isAdminFromAuth = data?.user?.app_metadata?.role === 'admin' || false;

      // CRITICAL: Check account status BEFORE allowing login to proceed
      try {
        const { data: profileInfo, error: profileError } = await supabase
          .from('profiles')
          .select('status, role')
          .eq('id', data.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error checking profile status during signIn:', profileError);
          // If we can't check status, err on the side of caution
          await supabase.auth.signOut();
          return {
            data: null,
            error: new Error('Erro no perfil. Entre em contacto com o suporte.'),
            isAdmin: false
          };
        }

        // If account is inactive, sign out immediately and return error
        if (profileInfo?.status === 'inactive') {
          console.log('User account is inactive during signIn, preventing login...');
          await supabase.auth.signOut();
          return {
            data: null,
            error: new Error('Conta suspensa. Entre em contacto com o suporte para reativação.'),
            isAdmin: false
          };
        }

        // Admin status: union of auth metadata and profiles.role, matching the
        // bootstrap path in fetchUserProfile so /admin/login agrees with the
        // rest of the app.
        const isAdminFromProfile = profileInfo?.role === 'admin';
        const isUserAdmin = isAdminFromAuth || isAdminFromProfile;

        console.log('SignIn success, status check passed:', {
          email,
          userId: data.user.id,
          status: profileInfo?.status,
          authRole: data?.user?.app_metadata?.role,
          profileRole: profileInfo?.role,
          isAdmin: isUserAdmin
        });

        return {
          data,
          error: null,
          isAdmin: isUserAdmin
        };
      } catch (statusCheckError) {
        console.error('Error during status check:', statusCheckError);
        // If status check fails, sign out and return error
        await supabase.auth.signOut();
        return {
          data: null,
          error: new Error('Erro de verificação. Tente novamente.'),
          isAdmin: false
        };
      }
    } catch (error) {
      console.error('SignIn error:', error);
      return { data: null, error, isAdmin: false };
    }
  };

  // Implementation for signInWithDartsBeat (new DartsBeat-first login flow)
  const signInWithDartsBeat = async (email: string, password: string): Promise<DartsBeatAuthResult> => {
    try {
      console.log('Attempting DartsBeat-first login...');

      // Call the dartsbeat-auth edge function
      const { data, error: invokeError } = await supabase.functions.invoke('dartsbeat-auth', {
        body: { email, password }
      });

      if (invokeError) {
        console.error('DartsBeat auth invoke error:', invokeError);
        return {
          success: false,
          error: 'Erro de conexão. Tente novamente.',
          isAdmin: false
        };
      }

      if (!data?.success) {
        // Map error codes to user-friendly messages
        const errorMessages: Record<string, string> = {
          'INVALID_DARTSBEAT_CREDENTIALS': 'Credenciais DartsBeat inválidas. Verifique o email e password.',
          'DARTSBEAT_UNAVAILABLE': 'Serviço DartsBeat indisponível. Tente novamente mais tarde.',
          'RATE_LIMIT_EXCEEDED': 'Demasiadas tentativas. Aguarde alguns minutos.',
          'MISSING_CREDENTIALS': 'Email e password são obrigatórios.',
          'INVALID_EMAIL': 'Formato de email inválido.',
          'PASSWORD_TOO_SHORT': 'A password deve ter pelo menos 6 caracteres.',
          'SERVICE_UNAVAILABLE': 'Serviço temporariamente indisponível.',
          'USER_CREATION_FAILED': 'Erro ao criar conta. Tente novamente.',
          'USER_LOOKUP_FAILED': 'Erro ao localizar a conta. Contacte o suporte.',
          'USER_UPDATE_FAILED': 'Erro ao atualizar a conta. Tente novamente.',
          'PROFILE_LINK_FAILED': 'Não foi possível ligar a sua conta DartsBeat. Tente novamente.',
          'EMAIL_ALREADY_LINKED': 'Este email DartsBeat já está associado a outra conta. Contacte o suporte.',
          'SESSION_CREATION_FAILED': 'Erro ao iniciar sessão. Tente novamente.',
          'EMAIL_NOT_ALLOWED': 'Este email ainda não está autorizado para o novo login.',
          'ORIGIN_NOT_ALLOWED': 'Acesso não autorizado a partir desta origem.',
          'DARTSBEAT_ERROR': 'Não foi possível validar a sua conta DartsBeat. Tente novamente ou contacte o suporte.',
          'DARTSBEAT_INVALID_RESPONSE': 'Resposta inválida do serviço DartsBeat. Tente novamente.',
          'SERVICE_ERROR': 'Erro de serviço. Tente novamente mais tarde.',
          'INVALID_JSON': 'Pedido inválido. Atualize a página e tente novamente.',
          'INTERNAL_ERROR': 'Ocorreu um erro interno. Tente novamente mais tarde.',
        };

        // Never surface a raw error code to the user: fall back to a generic
        // message when the code is unmapped. The raw code is still returned in
        // errorCode below for debugging / conditional UI handling.
        const errorMessage = errorMessages[data?.error] || 'Erro de autenticação. Tente novamente ou contacte o suporte.';
        console.warn('DartsBeat auth failed:', data?.error);

        return {
          success: false,
          error: errorMessage,
          errorCode: data?.error,
          isAdmin: false
        };
      }

      // Success - set the session
      if (data.session) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });

        if (sessionError) {
          console.error('Failed to set session:', sessionError);
          return {
            success: false,
            error: 'Erro ao iniciar sessão. Tente novamente.',
            isAdmin: false
          };
        }

        // Check admin role
        const { data: profileInfo } = await supabase
          .from('profiles')
          .select('role, status')
          .eq('id', data.user?.id)
          .maybeSingle();

        // Check if account is suspended
        if (profileInfo?.status === 'inactive') {
          await supabase.auth.signOut();
          return {
            success: false,
            error: 'Conta suspensa. Entre em contacto com o suporte.',
            isAdmin: false
          };
        }

        const isUserAdmin = profileInfo?.role === 'admin';

        console.log('DartsBeat auth successful:', {
          userId: data.user?.id,
          points: data.user?.points,
          isAdmin: isUserAdmin
        });

        return {
          success: true,
          isAdmin: isUserAdmin,
          points: data.user?.points
        };
      }

      return {
        success: false,
        error: 'Resposta inválida do servidor.',
        isAdmin: false
      };
    } catch (error) {
      console.error('signInWithDartsBeat error:', error);
      return {
        success: false,
        error: 'Erro inesperado. Tente novamente.',
        isAdmin: false
      };
    }
  };

  // Implementation for resetPassword (used by ResetPasswordForm)
  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password'
      });

      return { data, error };
    } catch (error) {
      return { error };
    }
  };

  // Implementation for updateUserPassword (used by NewPasswordForm)
  const updateUserPassword = async (password: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      return { data, error };
    } catch (error) {
      return { error };
    }
  };

  // Admin role is now properly determined from user metadata only

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        session,
        login,
        logout,
        loading,
        isAdmin: isAdmin,
        isLoading: loading || !isInitialized,
        refreshUserProfile,
        signIn,
        signInWithDartsBeat,
        resetPassword,
        updateUserPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
